import { useAxios } from 'hooks/useAxios';
import { isAxiosError } from 'axios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { Vendor } from 'sb/models/vendor';
import { PriceList } from 'sb/models/PriceList';
import { BASE_URL } from 'sb/shared/constants';
import { BASE_URL_V2 } from 'shared/constants';

const VITE_APP_UNSECURE_ROUTE = import.meta.env.VITE_APP_UNSECURE_ROUTE ?? 'secure';

export interface SalesforceSupplierSearchResult {
  Id: string;
  Name: string;
  Website?: string | null;
  Deals_as_Primary_Vendor_Last_24_Mos_RH__c?: number | null;
}

export interface VendorLookupSearchResult {
  id: number;
  name: string;
  domain?: string | null;
  salesforce_id?: string | null;
  deal_count: number;
}

export interface VendorSalesforceLinkResponse {
  vendor_id: number;
  salesforce_account_id: string | null;
  override_applied: boolean;
  detached_vendor_ids: number[];
}

export interface VendorCreatePayload {
  name: string;
  profile?: string;
  domain?: string;
  salesforce_id?: string;
  industry?: string;
  sub_industry?: string;
  symbol?: string;
  annual_revenue?: number;
  fiscal_year_end?: string;
  is_pinpoint?: boolean;
}

export interface VendorCreateResponse {
  id: number;
  name: string;
  profile?: string | null;
  domain?: string | null;
  salesforce_id?: string | null;
  industry?: string | null;
  sub_industry?: string | null;
  symbol?: string | null;
  annual_revenue?: number | null;
  fiscal_year_end?: string | null;
  is_pinpoint: boolean;
}

export interface SalesforceSupplierAccountWritePayload {
  Name: string;
  Website?: string;
}

export interface SalesforceSupplierAccountWriteResponse {
  id: string;
  success: boolean;
  errors: string[];
}

export enum VendorMigrationResolutionAction {
  MapToTargetProduct = 'MAP_TO_TARGET_PRODUCT',
  MoveSourceProductToTargetVendor = 'MOVE_SOURCE_PRODUCT_TO_TARGET_VENDOR'
}

export enum VendorMigrationMatchStrategy {
  Code = 'CODE',
  Name = 'NAME',
  Manual = 'MANUAL',
  MoveSourceProduct = 'MOVE_SOURCE_PRODUCT'
}

export interface VendorMigrationProductCandidate {
  id: number;
  code: string;
  name: string;
}

export interface VendorMigrationAutoMatch {
  source_product: VendorMigrationProductCandidate;
  target_product: VendorMigrationProductCandidate;
  strategy: VendorMigrationMatchStrategy;
  affected_line_item_count: number;
  affected_deal_count: number;
}

export interface VendorMigrationUnresolvedProduct {
  source_product: VendorMigrationProductCandidate;
  reason: string;
  candidate_target_products: VendorMigrationProductCandidate[];
  affected_line_item_count: number;
  affected_deal_count: number;
}

export interface VendorMigrationPreviewRequest {
  source_vendor_id: number;
  target_vendor_id: number;
}

export interface VendorMigrationPreviewResponse {
  preview_hash: string;
  summary: {
    deal_count: number;
    source_deal_party_count: number;
    line_item_count: number;
    linked_contract_transaction_count: number;
    auto_match_count: number;
    unresolved_product_count: number;
  };
  auto_matches: VendorMigrationAutoMatch[];
  unresolved_products: VendorMigrationUnresolvedProduct[];
  warnings: string[];
}

export interface VendorMigrationProductResolution {
  source_product_id: number;
  action: VendorMigrationResolutionAction;
  target_product_id?: number;
}

export interface VendorMigrationExecuteRequest extends VendorMigrationPreviewRequest {
  preview_hash: string;
  resolutions?: VendorMigrationProductResolution[];
}

export interface VendorMigrationExecuteResponse {
  summary: {
    migrated_deal_count: number;
    in_place_deal_party_updates: number;
    merged_deal_party_count: number;
    rewired_deal_proposal_count: number;
    rewired_document_count: number;
    remapped_line_item_count: number;
    remapped_contract_transaction_count: number;
    moved_product_count: number;
  };
  warnings: string[];
}

export interface VendorMigrationTargetProductSearchQuery {
  target_vendor_id: number;
  str: string;
  limit?: number;
}

export interface VendorMigrationTargetProductSearchResponse {
  products: VendorMigrationProductCandidate[];
}

export const useVendorApi = () => {
  const { axios } = useAxios(BASE_URL);

  const searchVendors = async (reseller: boolean, str: string, limitToVendorDynamics?: boolean) => {
    try {
      const res = await axios.get(
        `/${!reseller ? 'vendor' : 'reseller'}/search?str=${encodeURIComponent(str)}${
          limitToVendorDynamics !== undefined ? `&vdi=${+limitToVendorDynamics}` : ''
        }`,
        {
          baseURL: BASE_URL_V2
        }
      );
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Search Salesforce supplier accounts by name.
   * @param {string} str Search string to match supplier account names.
   * @returns {Promise<SalesforceSupplierSearchResult[]>} Matching Salesforce supplier accounts.
   */
  const searchSalesforceSuppliers = async (str: string): Promise<SalesforceSupplierSearchResult[]> => {
    try {
      const res = await axios.post(
        '/salesforce/suppliers/search',
        {
          str
        },
        {
          baseURL: BASE_URL_V2
        }
      );
      if (isAxiosError(res)) {
        throw res;
      }
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Search vendor lookup records by name, domain, or salesforce_id.
   * @param {string} str Search string to match vendor records.
   * @returns {Promise<VendorLookupSearchResult[]>} Matching vendor lookup records.
   */
  const searchVendorLookup = async (str: string): Promise<VendorLookupSearchResult[]> => {
    try {
      const res = await axios.get(`/vendor/search-basic?str=${encodeURIComponent(str)}`, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Select supplier info
   * @param id
   * @returns
   */
  const getVendor = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}`);
      return res.data;
    } catch (e) {
      return e;
    }
  };

  /**
   * Select vendor details
   * @param id
   * @returns
   */
  const getVendorDetails = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendors/${id}`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      return e;
    }
  };

  const getVendors = async (str?: string) => {
    try {
      const res = await axios.get(str ? `/secure/vendors?str=${str}` : `/secure/vendors`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      throw new Error(res.data?.message);
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const createVendor = async (payload: VendorCreatePayload): Promise<VendorCreateResponse> => {
    try {
      const res = await axios.post(`/vendor`, payload, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const updateVendor = async (id: number, values: Partial<Vendor>) => {
    try {
      const res = await axios.patch(`/secure/vendor/${id}`, values);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      throw new Error(res.data?.message);
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorCategories = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/categories`);
      return res.data;
    } catch (e) {
      return e;
    }
  };

  const getVendorContracts = async (id: string) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/contracts`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorProducts = async (id: number, str: string, unverified?: boolean) => {
    try {
      const res = await axios.get(
        `/secure/vendor/${id}/products/search?str=${str}${unverified ? '&is_confirmed=0' : ''}`
      );
      if (res.data?.status === 'error') {
        throw new Error(res.data?.message);
      }
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const mergeVendorProducts = async (
    id: number,
    data: { product_ids: number[]; product_code: string; product_name: string; is_confirmed: boolean }
  ) => {
    try {
      const res = await axios.post(
        `/product/vendor/${id}/merge`,
        {
          productIds: data.product_ids,
          productCode: data.product_code,
          productName: data.product_name,
          isConfirmed: !!data.is_confirmed
        },
        { baseURL: BASE_URL_V2 }
      );
      return res.data;
    } catch (e) {
      return AxiosErrorHandler(e);
    }
  };

  const updateVendorProduct = async (id: number, productId: number, data: any) => {
    try {
      const res = await axios.patch(`/product/vendor/${id}/product/${productId}`, data, {
        baseURL: BASE_URL_V2
      });
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message) || 'Failed to update vendor product');
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateVendorProductConfirmed = async (id: number, productId: number, isConfirmed: boolean) => {
    try {
      const res = await axios.put(`/secure/vendor/${id}/product/${productId}/is_confirmed`, {
        is_confirmed: isConfirmed
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateVendorProductCategories = async (id: number, productId: number, categories: any[]) => {
    try {
      const res = await axios.put(`/secure/vendor/${id}/product/${productId}/categories`, { categories });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const batchUpdateVendorProductCategories = async (id: number, productIds: (number | string)[], categories: any[]) => {
    try {
      const res = await axios.put(`/secure/vendor/${id}/products/categories`, {
        categories,
        product_ids: productIds
      });

      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorDealDynamics = async (id: number, categoryId?: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/dealDynamics${categoryId ? `?category_id=${categoryId}` : ''}`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const createVendorDealDynamics = async (id: number, supplierDynamics: any, categoryId: any, profile: any) => {
    try {
      const res = await axios.post(`/secure/vendor/${id}/dealDynamics`, {
        supplierDynamics,
        category_id: categoryId,
        profile
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorPriceLists = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/price_lists`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorPriceList = async (id: number, productListId: number, str: string, purchaseLevelId?: number) => {
    let url = `/secure/vendor/${id}/price_list/${productListId}?str=${str}`;

    if (purchaseLevelId !== undefined) {
      url += `&purchase_level_id=${purchaseLevelId}`;
    }

    try {
      const res = await axios.get(url);
      if (res.data?.status === 'error') {
        throw new Error(res.data?.message || 'Error');
      }
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateVendorPriceList = async (id: number, values: Partial<PriceList>) => {
    try {
      const res = await axios.put(`/secure/vendor/price_lists/${id}`, values);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const deleteVendorPriceList = async (id: number) => {
    try {
      const res = await axios.delete(`/secure/vendor/price_lists/${id}`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorProductsByName = async (id: number, products: any[]) => {
    try {
      const res = await axios.post(`/secure/vendor/${id}/products_by_names`, { products });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Search for products of a given vendor
   * @param vendor_id
   * @param str
   */
  const searchVendorProducts = async (vendor_id: number, str: string) => {
    if (str.length > 1) {
      try {
        const res = await axios.get(`/secure/vendor/${vendor_id}/search?str=${str}`);
        return res.data;
      } catch (e) {
        AxiosErrorHandler(e);
      }
    }
  };

  /**
   * Retrieve a vendor's ESG scores
   * @param vendor_id
   */
  const fetchVendorESGScores = async (vendor_id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${vendor_id}/esg`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      return Promise.reject(Error(res.data?.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Get Vendor or Client from salesforce id
   * @param { string } sf_account_id
   * @returns
   */
  const getVendorBySalesforceId = async (sf_account_id: string) => {
    try {
      const res = await axios.get(`/secure/vendor/sf_account/${sf_account_id}`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Explicitly links or unlinks a Vantage vendor to a Salesforce Account.
   * @param {number} vendorId Internal vendor id.
   * @param {string | null} salesforceAccountId Salesforce Account Id to set, or null to clear.
   * @param {boolean} override Whether to force reassignment when account is already linked.
   * @returns {Promise<VendorSalesforceLinkResponse>} Link operation result.
   */
  const setVendorSalesforceLink = async (
    vendorId: number,
    salesforceAccountId: string | null,
    override = false
  ): Promise<VendorSalesforceLinkResponse> => {
    try {
      const res = await axios.post(
        `/vendor/${vendorId}/salesforce-link`,
        {
          salesforce_account_id: salesforceAccountId,
          override
        },
        {
          baseURL: BASE_URL_V2
        }
      );
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Deletes a vendor record by internal id.
   * @param {number} vendorId Internal vendor id.
   * @returns {Promise<void>} Empty response when delete succeeds.
   */
  const deleteVendor = async (vendorId: number): Promise<void> => {
    try {
      await axios.delete(`/vendor/${vendorId}`, {
        baseURL: BASE_URL_V2
      });
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Creates a Salesforce supplier Account with v1 minimum fields.
   * @param {SalesforceSupplierAccountWritePayload} payload Supplier create payload.
   * @returns {Promise<SalesforceSupplierAccountWriteResponse>} Create operation result.
   */
  const createSalesforceSupplierAccount = async (
    payload: SalesforceSupplierAccountWritePayload
  ): Promise<SalesforceSupplierAccountWriteResponse> => {
    try {
      const res = await axios.post(`/salesforce/suppliers`, payload, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Renames a Salesforce supplier Account name.
   * @param {string} accountId Salesforce Account Id.
   * @param {string} name New account name.
   * @returns {Promise<SalesforceSupplierAccountWriteResponse>} Rename operation result.
   */
  const renameSalesforceSupplierAccount = async (
    accountId: string,
    name: string
  ): Promise<SalesforceSupplierAccountWriteResponse> => {
    try {
      const res = await axios.patch(
        `/salesforce/supplier/${accountId}/name`,
        {
          Name: name
        },
        {
          baseURL: BASE_URL_V2
        }
      );
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Builds a vendor migration preview for source and target vendors.
   * @param {VendorMigrationPreviewRequest} payload Migration preview payload.
   * @returns {Promise<VendorMigrationPreviewResponse>} Preview details and hash.
   */
  const previewVendorMigration = async (
    payload: VendorMigrationPreviewRequest
  ): Promise<VendorMigrationPreviewResponse> => {
    try {
      const res = await axios.post(`/vendor/migration/preview`, payload, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Executes vendor migration using a previously generated preview hash.
   * @param {VendorMigrationExecuteRequest} payload Migration execution payload.
   * @returns {Promise<VendorMigrationExecuteResponse>} Execution summary and warnings.
   */
  const executeVendorMigration = async (
    payload: VendorMigrationExecuteRequest
  ): Promise<VendorMigrationExecuteResponse> => {
    try {
      const res = await axios.post(`/vendor/migration/execute`, payload, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Searches target-vendor products for migration manual matching.
   * @param {VendorMigrationTargetProductSearchQuery} query Search query.
   * @returns {Promise<VendorMigrationTargetProductSearchResponse>} Matching products.
   */
  const searchMigrationTargetProducts = async (
    query: VendorMigrationTargetProductSearchQuery
  ): Promise<VendorMigrationTargetProductSearchResponse> => {
    try {
      const qs = new URLSearchParams({
        target_vendor_id: `${query.target_vendor_id}`,
        str: query.str,
        ...(query.limit ? { limit: `${query.limit}` } : {})
      });
      const res = await axios.get(`/vendor/migration/target-products?${qs.toString()}`, {
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Update Vendor Category
   * @param supplier_id
   * @param supplier_category_id
   * @param values
   * @param values.profile
   * @param values.category
   */
  const updateSupplierCategory = async (
    supplier_id: number,
    supplier_category_id: number,
    values: { profile?: string; category?: string }
  ) => {
    try {
      const res = await axios.patch(`/secure/supplier/${supplier_id}/category/${supplier_category_id}`, values);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Create Vendor Category
   * @param supplier_id
   * @param values
   * @param values.profile
   * @param values.category
   */
  const createSupplierCategory = async (supplier_id: number, values: { profile?: string; category?: string }) => {
    try {
      const res = await axios.post(`/secure/supplier/${supplier_id}/category`, values);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Vendor Logo filename field
   * @param vendor_id
   * @param logo_filename
   */
  const updateSupplierLogoFilename = async (vendor_id: number, logo_filename: string) => {
    try {
      const res = await axios.patch(`/secure/supplier/${vendor_id}/logoFilename`, { logo_filename });
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update competitor
   * @param id
   * @param values
   * @param values.competitor_id
   * @param values.competitor
   * @param values.supplier_category_id
   */
  const updateCompetitor = async (
    id: number,
    values: { competitor_id?: number; competitor: string; supplier_category_id?: number }
  ) => {
    try {
      const res = await axios.patch(`/secure/competitor/${id}`, values);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update competitor
   * @param id
   */
  const deleteCompetitor = async (id: number) => {
    try {
      const res = await axios.delete(`/secure/competitor/${id}`);
      if (res.data?.status === 'success') {
        return true;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Create competitor
   * @param id
   * @param values
   * @param values.competitor_id
   * @param values.competitor
   * @param values.supplier_category_id
   */
  const addCompetitor = async (
    id: number,
    values: { competitor_id?: number; competitor: string; supplier_category_id?: number }
  ) => {
    try {
      const res = await axios.post(`/secure/supplier/${id}/competitor`, values);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  return {
    searchVendors,
    searchSalesforceSuppliers,
    searchVendorLookup,
    getVendor,
    getVendorDetails,
    getVendors,
    createVendor,
    updateVendor,
    getVendorCategories,
    getVendorContracts,
    getVendorProducts,
    mergeVendorProducts,
    updateVendorProduct,
    updateVendorProductConfirmed,
    updateVendorProductCategories,
    batchUpdateVendorProductCategories,
    getVendorDealDynamics,
    createVendorDealDynamics,
    getVendorPriceLists,
    getVendorPriceList,
    updateVendorPriceList,
    deleteVendorPriceList,
    getVendorProductsByName,
    searchVendorProducts,
    fetchVendorESGScores,
    getVendorBySalesforceId,
    setVendorSalesforceLink,
    deleteVendor,
    createSalesforceSupplierAccount,
    renameSalesforceSupplierAccount,
    previewVendorMigration,
    executeVendorMigration,
    searchMigrationTargetProducts,
    updateSupplierCategory,
    createSupplierCategory,
    updateSupplierLogoFilename,
    updateCompetitor,
    deleteCompetitor,
    addCompetitor
  };
};
